import React from 'react';
import { Box, Grid, Tooltip, Typography } from '@mui/material';
import { Cancel, Check, Delete, Refresh, TextSnippet } from '@mui/icons-material';
import { Badge, Button } from "@deere/fuel-react";
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
} from "material-react-table";
import { FileDownloadOutlined } from '@mui/icons-material';
import { useExportExcel } from './hooks/useExportExcel';
const TransactionsTable = ({ 
  columns, 
  filteredTransactions, 
  fetchTransactions, 
  showToast, 
  handleApproveClick, 
  handleDeleteClick, 
  handleRejectClick,
  initialSorting, 
  disableRowExpansion,
  disableRowSelection,  
  manualPagination = false,
  pagination,
  onPaginationChange,
  rowCount,
  initialColumnPinning,  
  exportConfig,
  exportFilename, 
}) => {
  const formatMessageAsArray = (message) => {
    return message.split("\n");
  };

  // Prepare initial state with optional sorting and pagination
  const initialState = {
    density: "compact",
    ...(initialSorting && { sorting: initialSorting }),
    ...(initialColumnPinning && { columnPinning: initialColumnPinning })
  };

  // If not using manual pagination, set default pagination in initial state
  if (!manualPagination) {
    initialState.pagination = { pageIndex: 0, pageSize: 100 };
  }
const { exportToExcel } = useExportExcel(exportConfig, exportFilename);
  return (
    <MaterialReactTable
    muiPaginationProps={{
    rowsPerPageOptions: [25, 50, 100, 200, 500],
  }}
      mrtTheme={() => ({ baseBackgroundColor: "rgb(255, 255, 255)" })}
      muiTableHeadCellProps={() => ({
        sx: { 
          borderBottom: "1px solid rgb(224, 224, 224)",
          '& .Mui-TableHeadCell-Content': {
            whiteSpace: 'normal',
            overflow: 'visible',
          },
          '& .Mui-TableHeadCell-Content-Wrapper': {
            whiteSpace: 'normal',
            overflow: 'visible',
          },
          '&[data-pinned="true"]::before': {
            boxShadow: 'none',
          },
          '&[data-pinned="true"]::after': {
            boxShadow: 'none',
          }
        },
      })}
      muiTableBodyCellProps={() => ({
        sx: { 
          borderBottom: "1px solid rgb(224, 224, 224)",
          '&[data-pinned="true"]::before': {
            boxShadow: 'none',
          },
          '&[data-pinned="true"]::after': {
            boxShadow: 'none',
          }
        },
      })}
      enableRowSelection={disableRowSelection ? false : true}
      enableColumnPinning={true}
      enablePinning={true}
      enableColumnActions={true}
      columns={columns}
       selectAllMode="all"
      initialState={initialState}
      data={filteredTransactions}
      enableRowExpansion={disableRowExpansion ? false : true}
      enableRowVirtualization={false}
      enablePagination={true}
      enableStickyHeader={true}
      enableStickyFooter={true}
      // Manual pagination props
      manualPagination={manualPagination}
      {...(manualPagination && {
        rowCount: rowCount,
        onPaginationChange: onPaginationChange,
        state: { pagination: pagination }
      })}
      muiTableContainerProps={{ 
        sx: { 
          maxHeight: '70vh',
          overflow: 'auto',
          '& .MuiTableCell-root[data-pinned="true"]': {
            '&::before': {
              boxShadow: 'none !important',
            },
            '&::after': {
              boxShadow: 'none !important',
            }
          }
        } 
      }}
      muiTablePaperProps={{
        sx: {
          boxShadow: 'none',
        }
      }}
      renderToolbarInternalActions={({ table }) => (
        <Box>
          <MRT_ToggleDensePaddingButton table={table} /> {/* eslint-disable-line react/jsx-pascal-case */}
          <MRT_ToggleFullScreenButton table={table} /> {/* eslint-disable-line react/jsx-pascal-case */}
          <MRT_ToggleFiltersButton table={table} /> {/* eslint-disable-line react/jsx-pascal-case */}
          <MRT_ToggleGlobalFilterButton table={table} /> {/* eslint-disable-line react/jsx-pascal-case */}
          <Tooltip title="Refresh Data">
            <Button
              sx={{ borderRadius: "50%", width: "40px", height: "40px" }}
              variant="contained1"
              color="success"
              onClick={() => fetchTransactions()}
            >
              <Refresh sx={{ color: "#767676" }} />
            </Button>
          </Tooltip>

          {exportConfig && (
            <Tooltip title="Export to Excel">
              <Button
                sx={{ borderRadius: "50%", width: "40px", height: "40px" }}
                variant="contained1"
                onClick={() => {
                  if (table.getFilteredRowModel().rows.length === 0) {
                    showToast('No data available to export', 'warning');
                    return;
                  }
                  exportToExcel(table);
                }}
              >
                <FileDownloadOutlined sx={{ color: "green" }} />
              </Button>
            </Tooltip>
          )}

          {/* Approve Transaction Button will be visible if 'handleApproveClick' passed as props*/}
          { handleApproveClick && (
            <Tooltip title="Approve Transaction">
              <Button
                variant="contained1"
                sx={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  marginLeft: "8px",
                }}
                onClick={() => {
                  if (table.getSelectedRowModel().rows.length === 0) {
                    showToast(<span>No data selected !!!</span>, "warning");
                  } else {
                    handleApproveClick(table.getSelectedRowModel().rows);
                  }
                }}
              >
                <Check
                  sx={{
                    color:
                      table.getSelectedRowModel().rows.length === 0
                        ? "#767676"
                        : "green",
                  }}
                />
              </Button>
            </Tooltip>
          )}

          {/* Delete Transaction Button will be visible if 'handleDeleteClick' passed as props*/}
          { handleDeleteClick && (
            <Tooltip title="Delete Transaction">
              <Button
                sx={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  marginLeft: "8px",
                }}
                variant="contained1"
                onClick={() => {
                  if (table.getSelectedRowModel().rows.length === 0) {
                    showToast(<span>No data selected !!!</span>, "warning");
                  } else {
                    handleDeleteClick(table.getSelectedRowModel().rows);
                  }
                }}
              >
                <Delete
                  sx={{
                    color:
                      table.getSelectedRowModel().rows.length === 0
                        ? "grey"
                        : "#C21020",
                  }}
                />
              </Button>
            </Tooltip>
          )}

          {/* Reject Transaction Button will be visible if 'handleRejectClick' passed as props*/}
          { handleRejectClick && (
            <Tooltip title="Reject Transaction">
              <Button
                sx={{
                  borderRadius: "50%",
                  width: "40px",
                  height: "40px",
                  marginLeft: "8px",
                }}
                variant="contained1"
                onClick={() => {
                  if (table.getSelectedRowModel().rows.length === 0) {
                    showToast(<span>No data selected !!!</span>, "warning");
                  } else {
                    handleRejectClick(table.getSelectedRowModel().rows);
                  }
                }}
              >
                <Cancel
                  sx={{
                    color:
                      table.getSelectedRowModel().rows.length === 0
                        ? "grey"
                        : "#C21020",
                  }}
                />
              </Button>
            </Tooltip>
          )}
        </Box>
      )}
      {...(!disableRowExpansion && {
        renderDetailPanel: ({ row }) => (
          <Box sx={{ padding: 2 }}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h2" gutterBottom>
                  <Badge startIcon={<TextSnippet />} variant="info">
                    <strong>
                      <u>{row.original.transactionType} Memo Request</u>
                    </strong>
                  </Badge>
                </Typography>
              </Grid>
              {Object.entries(row.original).map(
              ([key, value]) =>
                key !== "debitMemoRequestItem" &&
                key !== "creditMemoRequestItem" &&
                key !== "itemText" &&
                key !== "currencydisplay" &&
                key !== "itemTextDisplay" &&
                key !== "totalAmount" &&
                key !== "duplicateMessage" &&
                key !== "statusText" &&
                key !== "profitCenter" &&
                key !== "isHighAmount" &&
                key !== "threshold" &&
                key !== "isSapNumberUploaded" &&
                key !== "sapNumber" &&
                key !== "accountingStatus" &&
                key !== "accountingUserId" &&
                key !== "reasonOfRejection" &&
                key !== "accountingApprovalTimestamp"
                
                && (
                  <Grid item xs={6} sm={3} key={key}>
                    <Typography variant="body2">
                      <strong>
                        {key.replace(/([A-Z])/g, " $1").toLocaleLowerCase()}:
                      </strong>{" "}
                      {key === "message" ? (
                        <div>
                          {formatMessageAsArray(value).map((line, index) => (
                            <div key={index}>{line}</div>
                          ))}
                        </div>
                      ) : (
                        value
                      )}
                    </Typography>
                  </Grid>
                )
            )}
            {/* Conditionally show Profit Center field only if it has a value */}
            {(() => {
              const profitCenter = row.original.profitCenter || row.original.creditMemoRequest?.profitCenter || row.original.debitMemoRequest?.profitCenter;
              return profitCenter && profitCenter !== "N/A" ? (
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2">
                    <strong>profit center:</strong> {profitCenter}
                  </Typography>
                </Grid>
              ) : null;
            })()}
            {/* Conditionally show Reason of Rejection only if status is REJECTED and has value */}
            {(() => {
              const reasonOfRejection = row.original.reasonOfRejection;
              const accountingStatus = row.original.accountingStatus;
              return accountingStatus === "REJECTED" && reasonOfRejection ? (
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2">
                    <strong>reason of rejection:</strong> {reasonOfRejection}
                  </Typography>
                </Grid>
              ) : null;
            })()}
            {/* Conditionally show Accounting Approval Timestamp if status is not NOT_APPLICABLE and has value */}
            {(() => {
              const accountingApprovalTimestamp = row.original.accountingApprovalTimestamp;
              const accountingStatus = row.original.accountingStatus;
              return accountingStatus !== "NOT_APPLICABLE" && accountingApprovalTimestamp ? (
                <Grid item xs={6} sm={3}>
                  <Typography variant="body2">
                    <strong>accounting approval timestamp:</strong> {accountingApprovalTimestamp}
                  </Typography>
                </Grid>
              ) : null;
            })()}
            <Grid item xs={12} sx={{ marginTop: 2 }}>
              <Typography variant="h2" gutterBottom>
                <Badge startIcon={<TextSnippet />} variant="info">
                  <strong>
                    <u>{row.original.transactionType} Memo Request Items</u>
                  </strong>
                </Badge>
              </Typography>
            </Grid>
            {row.original.debitMemoRequestItem &&
              row.original.debitMemoRequestItem.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={6} sm={3}>
                    {Object.entries(item).map(
                      ([key, value]) =>
                        key !== "itemText" &&
                        key !== "debitMemoRequestItemConditions" && (
                          <Typography variant="body2" key={key}>
                            <strong>
                              {key.replace(/([A-Z])/g, " $1").toLocaleLowerCase()}:
                            </strong>{" "}
                            {value}
                          </Typography>
                        )
                    )}
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Item Text:</strong>
                    </Typography>
                    {item.itemText.map((text, textIndex) => (
                      <Typography
                        variant="body2"
                        key={textIndex}
                        sx={{ marginLeft: 2 }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h2" gutterBottom>
                      <Badge startIcon={<TextSnippet />} variant="info">
                        <strong>
                          <u>Debit Memo Request Items Conditions</u>
                        </strong>
                      </Badge>
                    </Typography>
                    {item.debitMemoRequestItemConditions.map(
                      (condition, condIndex) => (
                        <Box key={condIndex} sx={{ marginLeft: 2 }}>
                          {Object.entries(condition).map(([key, value]) => (
                            <Typography variant="body2" key={key}>
                              <strong>
                                {key.replace(/([A-Z])/g, " $1").toLocaleLowerCase()}:
                              </strong>{" "}
                              {value}
                            </Typography>
                          ))}
                        </Box>
                      )
                    )}
                  </Grid>
                </React.Fragment>
              ))}
            {row.original.creditMemoRequestItem &&
              row.original.creditMemoRequestItem.map((item, index) => (
                <React.Fragment key={index}>
                  <Grid item xs={6} sm={3}>
                    {Object.entries(item).map(
                      ([key, value]) =>
                        key !== "itemText" &&
                        key !== "creditMemoRequestItemConditions" && (
                          <Typography variant="body2" key={key}>
                            <strong>
                              {key.replace(/([A-Z])/g, " $1").toLocaleLowerCase()}:
                            </strong>{" "}
                            {value}
                          </Typography>
                        )
                    )}
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2">
                      <strong>Item Text:</strong>
                    </Typography>
                    {item.itemText.map((text, textIndex) => (
                      <Typography
                        variant="body2"
                        key={textIndex}
                        sx={{ marginLeft: 2 }}
                      >
                        {text}
                      </Typography>
                    ))}
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="h2" gutterBottom>
                      <Badge startIcon={<TextSnippet />} variant="info">
                        <strong>
                          <u>Credit Memo Request Items Conditions</u>
                        </strong>
                      </Badge>
                    </Typography>
                    {item.creditMemoRequestItemConditions.map(
                      (condition, condIndex) => (
                        <Box key={condIndex} sx={{ marginLeft: 2 }}>
                          {Object.entries(condition).map(([key, value]) => (
                            <Typography variant="body2" key={key}>
                              <strong>
                                {key.replace(/([A-Z])/g, " $1").toLocaleLowerCase()}:
                              </strong>{" "}
                              {value}
                            </Typography>
                          ))}
                        </Box>
                      )
                    )}
                  </Grid>
                </React.Fragment>
              ))}
          </Grid>
        </Box>
      )})}
    />
  );
};

export default TransactionsTable;